import { createRouter, createWebHistory } from 'vue-router'
import { UserRole } from '@atendafilliados/shared'
import { useAuthStore } from '@/stores/auth'
import { isNativePlatform } from '@/lib/capacitor'
import { landingRoute, storefrontRoute, adminRoutes, appRoutes, authRoutes, publicRedirectRoutes, notFoundRoute } from './routes'

const router = createRouter({
  history: createWebHistory(),
  routes: [landingRoute, storefrontRoute, ...authRoutes, ...publicRedirectRoutes, ...appRoutes, ...adminRoutes, notFoundRoute],
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    return { top: 0, behavior: 'smooth' }
  },
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // 0) Rotas públicas de vitrine e redirecionamento
  if (to.name === 'storefront' || to.name === 'public-smart-group-redirect' || to.name === 'public-short-link-redirect') {
    return true
  }

  // App nativo (Capacitor): a landing page não faz sentido no app — vai
  // direto ao painel (que redireciona ao login se não autenticado).
  if (isNativePlatform() && to.name === 'landing') {
    return { name: 'dashboard' }
  }

  // 1) Resolve tenant context para qualquer rota autenticada sob /app/*
  //    ou /admin/*. Para SUPER_ADMIN sem `currentCompanyId` no token, isso
  //    busca `/api/companies?pageSize=1` e marca a primeira como ativa.
  //    Idempotente: se já houver `currentCompanyId`, retorna imediatamente.
  if ((to.meta.layout === 'app') && auth.isAuthenticated) {
    await auth.ensureCompanyContext()
    // Liga o realtime WebSocket após resolver o contexto da empresa:
    // o store realtime depende de currentCompanyId para fazer subscribe,
    // e o QR/status de novas conexões chega em tempo real (antes do F5).
    try {
      const { useRealtimeStore } = await import('@/stores/realtime')
      useRealtimeStore().connect()
    } catch {
      /* realtime offline não deve bloquear a navegação */
    }
  }

  // Auth routes: redirect already-authenticated users to /app.
  if (to.meta.layout === 'auth') {
    if (auth.isAuthenticated && to.name !== 'reset-password') {
      return { name: 'dashboard' }
    }
    return true
  }

  // Protected routes require authentication.
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  // Role-based gates.
  if (to.meta.requiresRole && auth.user?.role !== to.meta.requiresRole && auth.user?.role !== UserRole.SUPER_ADMIN) {
    return { name: 'dashboard' }
  }

  return true
})

router.afterEach((to) => {
  const title = to.meta.title ? `${to.meta.title} · LupyBot` : 'LupyBot'
  document.title = title
})

export default router
